<template>
  <div class="my-account">
    <h2>{{ $t('My account') }}</h2>
    <p v-html="$t('Keep your account info updated')"></p>
    <v-form v-model="valid"
            :class="'request-form' + (loading
                    ? ' disabled'
                    : ''
                )"
            v-on:submit.prevent="submit"
    >
      <div class="basic-info">
        <v-text-field
            prepend-icon="fa-user-circle"
            v-model="form.name"
            :label="$t('Name')"
            :rules="[form_rules.required]"
        ></v-text-field>
        <v-text-field
            prepend-icon="fa-at"
            v-model="form.email"
            :label="$t('Email address')"
            :rules="[form_rules.required, form_rules.email]"
        ></v-text-field>
        <v-text-field
            prepend-icon="fa-phone"
            v-model="form.phone_number"
            :label="$t('Phone number')"
            :rules="[form_rules.required]"
        ></v-text-field>
      </div>
      <v-text-field
          v-for="(value, key) in form.address"
          v-if="key !== 'state'"
          :key="key"
          v-model="form.address[key]"
          :label="$t(`form_label_address_${key}`)"
          :class="form.address.country === 'BR' ? 'br_address' : ''"
          :rules="(key !== 'line_2' && key !== 'complementary') ? [form_rules.required] : []"
          :disabled="key === 'country'"
      ></v-text-field>
      <v-select
          v-else
          :items="states"
          item-text="state"
          item-value="abbr"
          v-model="form.address.state"
          :label="$t('State')"
          :class="form.address.country === 'BR' ? 'br_address' : ''"
          :rules="[form_rules.required]"
      ></v-select>
      <v-alert
          v-if="error"
          :value="true"
          type="error"
          class="notranslate"
      >{{error}}</v-alert>
      <v-btn type="submit"
             style="color: #fff;"
             :loading="loading"
             :disabled="loading || !valid"
             color="accent"
      >{{ $t('Submit') }}</v-btn>
    </v-form>
    <v-snackbar
        v-model="snackbar"
        :bottom="true"
        :vertical="true"
        :timeout="4800"
    >
      {{ $t('Account successfully updated') }}
      <v-btn
          flat
          @click="snackbar = false"
      >{{ $t('close') }}</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { updateUser } from '../utils/api'
import { UPDATE_USER } from '../store/types'
import VTextField from 'vuetify/es5/components/VTextField/VTextField'
import VSelect from 'vuetify/es5/components/VSelect/VSelect'
import VForm from 'vuetify/es5/components/VForm/VForm'
import VSnackbar from 'vuetify/es5/components/VSnackbar/VSnackbar'
import VAlert from 'vuetify/es5/components/VAlert/VAlert'
import { brStates } from '../utils/strings'
import form_rules_mixin from "../utils/form_rules_mixin";
export default {
  name: 'MyAccount',
  components: {
    VForm,
    VTextField,
    VSelect,
    VSnackbar,
    VAlert
  },
  mixins: [
    form_rules_mixin
  ],
  data: () => ({
    valid: false,
    loading: false,
    error: false,
    snackbar: false,
    states: brStates
  }),
  computed: {
    form: function () {
      let user = this.$store.state.user
      return {
        ...user
      }
    }
  },
  methods: {
    submit: function () {
      this.error = false
      this.loading = true
      updateUser(this.form)
          .then(result => {
            this.$store.commit(UPDATE_USER, result)
            this.snackbar = true
          })
          .catch(error => {
            this.error = this.$t('There was a problem with your submission')
            console.log(error)
          })
          .finally(() => {
            this.loading = false
          })
    }
  }
}
</script>

<style lang="scss">
.my-account {
  width: 100%;
  max-width: 720px;
  padding: 48px 24px;
  margin: 0 auto;
  h2 {
    text-align: center;
    color: rgba(0, 0, 0, 0.87)
  }
  p {
    text-align: center;
    color: rgba(0,0,0,0.54);
    font-size: 16px;
    line-height: 1.5em;
    margin: 36px 0;
  }
  .v-form {
    .v-input {
      margin: 12px 0;
    }
    & > .v-input.br_address {
      width: calc(50% - 12px);
      float: left;
    }
    & > .v-input:nth-child(even) {
      margin-right: 24px;
    }
    &.disabled {
      pointer-events: none;
      opacity: 0.6;
    }
    button {
      display: block;
      margin: 24px auto 0;
    }
    .basic-info {
      clear: both;
      margin-bottom: 24px;
      .basic-info-image {
        float: left;
        margin-right: 24px;
        width: 215px;
        height: 215px;
        border-radius: 50%;
        position: relative;
        overflow: hidden;
        background-size: cover;
        .actions {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          opacity: 0;
          background: rgba(255,255,255,0.75);
          transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          color: #000;
          input {
            display: none;
          }
          button {
            margin: 0;
          }
        }
        &:hover .actions {
          opacity: 1;
        }
      }
    }
  }
}
</style>

<i18n>
  {
    "en": {
      "My account": "My account",
      "Name": "Name",
      "Email address": "Email address",
      "Phone number": "Phone number",
      "form_label_address_zipcode": "Zip code",
      "form_label_address_line_1": "Address line 1",
      "form_label_address_line_2": "Address line 2",
      "form_label_address_street": "Street",
      "form_label_address_street_number": "Street number",
      "form_label_address_complementary": "Address street complement",
      "form_label_address_neighborhood": "Neighborhood",
      "form_label_address_city": "City",
      "form_label_address_state": "State",
      "form_label_address_country": "Country",
      "There was a problem with your submission": "There was a problem with your submission, try again later.",
      "Account successfully updated": "Account successfully updated!",
      "Keep your account info updated": "Keep your account information up to date using this form.<br>The information entered here is used to process your payments and send emails, they are not publicly exposed. To manage your public profile information, go to the Profiles page on the side menu and select the desired profile."
    },
    "pt": {
      "My account": "Meu cadastro",
      "Name": "Name",
      "Email address": "Endereço de e-mail",
      "Phone number": "Número de telefone",
      "form_label_address_zipcode": "CEP",
      "form_label_address_line_1": "Linha de endereço 1",
      "form_label_address_line_2": "Linha de endereço 2",
      "form_label_address_street": "Logradouro",
      "form_label_address_street_number": "Número",
      "form_label_address_complementary": "Complemento",
      "form_label_address_neighborhood": "Bairro",
      "form_label_address_city": "Cidade",
      "form_label_address_state": "Estado",
      "form_label_address_country": "País",
      "There was a problem with your submission": "Houve um problema com o envio, tente novamente mais tarde.",
      "Account successfully updated": "Cadastro atualizado com sucesso!",
      "Keep your account info updated": "Mantenha atualizadas suas informações de cadastro por meio deste formulário.<br>As informações inseridas aqui são usadas para processar seus pagamentos e envio de emails, não são expostas publicamente. Para gerenciar as informações de seus perfis públicos, acesse a página Perfis no menu lateral e selecione o perfil desejado."
    }
  }
</i18n>
